<script lang="ts">
    import "chartjs-adapter-luxon";
    import { onDestroy, onMount } from "svelte";

    import { type IStatsController } from "../../controller/IStatsController";
    import { type AggregatedStat } from "../../gen/adprovisioning/index";
    import { Booking } from "../../model/Booking";
    import { Slot, SLOT_TYPES } from "../../model/Slot";
    import BookingMetaCard from "../fragments/BookingMetaCard.svelte";
    import SlotInfoCard from "../fragments/SlotInfoCard.svelte";
    import {
        convertMsToSString,
        extractFromTimeToTotalsMap,
        MyChartType,
    } from "../../utility/GraphHelper.svelte";
    import { type IBookingController } from "../../controller";
    import { ConfigurationManager } from "../../ConfigurationManager";
    import AssetImgCard from "../fragments/AssetImgCard.svelte";
    import OverallStatsRow from "../fragments/OverallStatsRow.svelte";
    import { parseIntervalStringToSeconds } from "../../utility/DateTimeHelper";
    import { DefaultErrorHandler } from "../../utility/ContextHelper.svelte";
    import type { Totals } from "../../model/StatsTotals";
    import ChartComponent from "../components/ChartComponent.svelte";
    import type { Game } from "../../model/Game";
    import GeoStats from "../components/GeoStats.svelte";
    import TimeSelectionComponent from "../components/TimeSelectionComponent.svelte";

    let { params = { id: undefined } } = $props();

    /**
     * The technical id of this booking
     */
    let bookingId: string = params["id"];
    // @TODO add game icon to game model

    let bookingController: IBookingController =
        ConfigurationManager.getInstance().bookingController;

    let booking: Booking = $state(undefined);
    let bookingSlot: Slot = $state(undefined);
    let bookingGame: Game = $state(undefined);

    let chartImpressionsLoading: boolean = $state(true);
    let chartDurationLoading: boolean = $state(true);
    let chartUniquePlayersLoading: boolean = $state(true);

    let impressionTimeseries: Map<Date, number> = $state();
    let uniqueUsersTimeseries: Map<Date, number> = $state();

    let durationTimeseriess: Map<Date, number>[] = $state();

    let aggregation = $state("10min");

    let to = $state(new Date(Date.now()));
    const _initIntervalSecs = parseIntervalStringToSeconds("10min");
    const _initFrom = new Date(Date.now());
    _initFrom.setSeconds(_initFrom.getSeconds() - 10 * _initIntervalSecs);
    let from: Date = $state(_initFrom);

    let autoUpdate: boolean = $state(true);
    let autoUpdateTrigger: Date = $state(undefined);

    // Handle auto update settings
    let intervalHandle: any;
    
    // Update trigger interval, will only update the timeframe
    $effect(() => {
        autoUpdateTrigger;

        if (autoUpdate) {
            const newTo = new Date(Date.now());
            const intervalInSeconds = parseIntervalStringToSeconds(aggregation);
            const newFrom = new Date(newTo.getTime());
            newFrom.setSeconds(newFrom.getSeconds() - 10 * intervalInSeconds);
            from = newFrom;
            to = newTo;
        }
    });

    // Recalculate `from` when aggregation changes in auto-update mode
    $effect(() => {
        aggregation;
        if (autoUpdate) {
            const intervalInSeconds = parseIntervalStringToSeconds(aggregation);
            const newFrom = new Date(to.getTime());
            newFrom.setSeconds(newFrom.getSeconds() - 10 * intervalInSeconds);
            from = newFrom;
        }
    });

    // Trigger, when from to or aggregation are changed or when autoUpdate changes the interval
    $effect(() => {
        
        to;
        aggregation;
        booking;

        updateData();
    });


    function updateData() {
        if (!booking) {
            return;
        }

        updateEarnings();

        chartImpressionsLoading = true;
        chartUniquePlayersLoading = true;
        chartDurationLoading = true;

        statsController
            .getImpressionStatsForBooking(bookingId, from, to, aggregation)
            .then((resultMapTotals: Map<Date, Totals>) => {
                // Convert the Time -> Totals map to a breakdown for count, unique and duration
                let resultBunch = extractFromTimeToTotalsMap(resultMapTotals);

                impressionTimeseries = resultBunch.count;
                // chartImpressions.update();

                uniqueUsersTimeseries = resultBunch.uniquePlayers;
            })
            .catch(DefaultErrorHandler)
            .finally(() => {
                chartImpressionsLoading = false;
                chartUniquePlayersLoading = false;
                chartDurationLoading = false;
            });

        statsController
            .getBookingImpressionDurations(bookingId, from, to, aggregation)
            .then((resultMaps: Map<Date, AggregatedStat>) => {
                let newDurations: Map<Date, number>[] = [];
                newDurations[0] = new Map();
                newDurations[1] = new Map();
                newDurations[2] = new Map();

                resultMaps.forEach((val: AggregatedStat, keyDate: Date) => {
                    newDurations[0].set(keyDate, val.max ? val.max : 0);
                    newDurations[1].set(keyDate, val.avg ? val.avg : 0);
                    newDurations[2].set(keyDate, val.min ? val.min : 0);
                });

                durationTimeseriess = newDurations;
            })
            .catch((err) => {
                console.log("Fetching impression stats Failed: " + err);
            })
            .finally(() => {
                chartDurationLoading = false;
            });
    }



    let statsController: IStatsController =
        ConfigurationManager.getInstance().statsController;

    let currentTotalEarnings: number = $state(undefined);
    function updateEarnings() {
        statsController
            .getTotalEarningForBooking(bookingId)
            .then((totalTurnover: number) => {
                currentTotalEarnings = totalTurnover;
            })
            .catch(DefaultErrorHandler);
    }

    onMount(() => {
        bookingController
            .getBooking(bookingId)
            .then((camp: Booking) => {
                booking = camp;

                booking.slot.then((slot: Slot) => {
                    bookingSlot = slot;
                    bookingSlot.game.then((game: Game) => {
                        bookingGame = game;
                    });
                });
            })
            .catch(DefaultErrorHandler);

        intervalHandle = setInterval(() => {
                if (autoUpdate) {
                    autoUpdateTrigger = new Date(Date.now());
                }
            }, 5000);


    });

    onDestroy(() => {
        clearInterval(intervalHandle);
    });
</script>

<!-- Title -->
<nav aria-label="breadcrumb">
    <ol class="breadcrumb">
        <li class="breadcrumb-item">
            <a href="#/bookings">All Bookings</a>
        </li>

        <li class="breadcrumb-item">
            <a href="#/game/{bookingSlot ? bookingSlot.gameId : ''}"
                >{bookingGame ? bookingGame.name : ""}</a
            >
        </li>

        <li class="breadcrumb-item active" aria-current="page">
            {booking ? booking.name : ""}
        </li>
    </ol>
</nav>
<h1 class="h2 mt-4 mb-7">
    Statistics for Booking <i>{booking ? booking.name : ""}</i>
</h1>

<OverallStatsRow {bookingId} totalEarnings={currentTotalEarnings}
></OverallStatsRow>

<TimeSelectionComponent
    bind:from
    bind:to
    bind:aggregation
    bind:autoUpdateMode={autoUpdate}
    class="row mt-4 px-2"
></TimeSelectionComponent>

<div class="row justify-content-between my-4 px-2">
    <ChartComponent
        title="Impressions"
        chartType={MyChartType.BAR}
        hasGradient={false}
        {aggregation}
        seriesCfg={[
            {
                label: "Number of Impressions",
                colorIdentifier: "primary",
            },
        ]}
        {from}
        {to}
        data={impressionTimeseries ? [impressionTimeseries] : undefined}
        chartLoading={chartImpressionsLoading}
        class="col-12 col-lg-4 d-flex px-2"
    ></ChartComponent>

    <ChartComponent
        title="Unique Players"
        {aggregation}
        chartType={MyChartType.BAR}
        hasGradient={false}
        seriesCfg={[
            {
                label: "Unique Players",
                colorIdentifier: "primary",
            },
        ]}
        {from}
        {to}
        data={uniqueUsersTimeseries ? [uniqueUsersTimeseries] : undefined}
        chartLoading={chartUniquePlayersLoading}
        class="col-12 col-lg-4 d-flex px-2"
    ></ChartComponent>

    <ChartComponent
        title="Impression Duration"
        {aggregation}
        seriesCfg={[
            {
                label: "Max",
                colorIdentifier: "success",
            },
            {
                label: "Avg",
                colorIdentifier: "secondary",
            },
            {
                label: "Min",
                colorIdentifier: "danger",
            },
        ]}
        valueConversionFunction={convertMsToSString}
        {from}
        {to}
        data={durationTimeseriess ? durationTimeseriess : undefined}
        chartLoading={chartDurationLoading}
        class="col-12 col-lg-4 d-flex px-2"
    ></ChartComponent>
</div>
<!-- / .row -->
<div class="d-flex row justify-content-between px-2">
    {#if booking}
        <BookingMetaCard {booking} class="col-12 col-lg-3 px-2"
        ></BookingMetaCard>
        {#if bookingSlot}
            {#if bookingSlot.type == SLOT_TYPES.COMPLEX}
                <SlotInfoCard
                    slot={bookingSlot}
                    class="col-12 col-lg-3 px-2"
                ></SlotInfoCard>
            {/if}    
            <div class="col-12 {bookingSlot.type == SLOT_TYPES.COMPLEX ? "col-lg-6" : "col-lg-9"} px-2">
                <div class="card h-100 ">
                    <div class="card-body">
                        <h3 class="card-title">Geo Location Stats</h3>
                        <GeoStats id={bookingId}  {from} {to}></GeoStats>
                    </div>
                </div>
            </div>
        {/if}
        
    {/if}
</div>
<div class="row mt-4 px-2">
    {#if booking}
        {#each booking.assets.filter(a => a.selected) as asset}
            {#if bookingSlot}
                {@const assetSlot = asset.refSlot && asset.refSlot !== bookingSlot.id && bookingSlot.slots
                    ? (bookingSlot.slots.find(s => s.id === asset.refSlot) ?? bookingSlot)
                    : bookingSlot}
                <SlotInfoCard
                    slot={assetSlot}
                    class="col-12 col-lg-3 px-2 py-4"
                ></SlotInfoCard>
                <AssetImgCard
                    assetUrl={asset.url}
                    classes="col-12 col-lg-3 px-2 py-4"
                    slot={assetSlot}
                ></AssetImgCard>
            {/if}
            
        {/each}
    {/if}
</div>
